import * as React from 'react';

export interface SplitPanelTheme {
    collapsedBgColor?: string;
    collapsedLabelColor?: string;
    collapsedWidth?: number;
    dividerWidth?: number;
    dividerColor?: string;
    dividerHoverColor?: string;
    dividerBorderColor?: string;
    dividerBorderWidth?: number;
    maxReachedColor?: string;
    fontFamily?: string;
    fontSize?: string;
    fontWeight?: number;
}

// Define an interface for the expected child props
interface SplitPanelChildProps {
    slot?: string;
    'data-btn-pos'?: string;
    'data-btn-open-offset'?: string;
    'data-btn-closed-offset'?: string;
}

type SplitPanelChild = React.ReactElement<SplitPanelChildProps>;

type SplitPanelProps = (
    | {
        split: 'v';
        collapseDirection: 'left' | 'right';
    }
    | {
        split: 'h';
        collapseDirection: 'up' | 'down';
    }
) & {
    initialSize?: number;
    collapsedSize?: number;
    resizable?: boolean;
    maxResize?: number;
    collapsed?: boolean;
    collapsible?: boolean;
    theme?: SplitPanelTheme;
    label?: React.ReactNode;
    labelAlign?: 'start' | 'center' | 'end';
    snapButtonTimeout?: number;
    style?: React.CSSProperties;
    showDepth?: boolean;
    resizeAssist?: boolean;
    slot?: string;
    children?: SplitPanelChild[];
    onOpen?: () => void;
    onClose?: () => void;
    animate?: boolean;
    animDurationMs?: number;
    animEasing?: string;
    fixedHeight?: string;
};

type SplitPanelInitProps = SplitPanelProps & {
    initialSize: number;
    collapsedSize: number;
    maxResize: number;
    collapsed: boolean;
    resizable: boolean;
    snapButtonTimeout?: number;
    theme: SplitPanelTheme;
    animate: boolean;
    animDurationMs: number;
    animEasing: string;
};

interface SplitPanelState {
    size: number;
    collapsed: boolean;
    prevSize: number;
    snapBtnPosLeft?: number;
    snapBtnPosTop?: number;
    snapBtnDisplay?: boolean;
    snapBtnIsPressed?: boolean;
    isResizing?: boolean;
    maxReached?: boolean;
    isAnimating?: boolean;
}

export default class SplitPanel extends React.Component<SplitPanelProps, SplitPanelState> {
    static defaultProps: SplitPanelInitProps = {
        split: 'v',
        collapseDirection: 'left',
        initialSize: 30,
        collapsedSize: 0,
        maxResize: 100,
        collapsed: false,
        collapsible: true,
        resizable: false,
        showDepth: false,
        resizeAssist: false,
        theme: {
            collapsedBgColor: 'white',
            // collapsedLabelColor: 'black',
            dividerWidth: 10,
            dividerColor: 'white',
            dividerHoverColor: 'aqua',
            dividerBorderColor: '#aaa',
            maxReachedColor: 'F85A3C',
            collapsedWidth: 30,
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
            fontSize: '1.125rem',
            fontWeight: 0
        },
        animate: true,
        animDurationMs: 400,
        animEasing: 'ease'
    };

    initProps: SplitPanelInitProps;

    // Allow current to be null by using HTMLDivElement | null
    panel: React.RefObject<HTMLDivElement | null>;
    divider: React.RefObject<HTMLDivElement | null>;
    dividerZindex = 1000;
    snapButtonContainer: React.RefObject<HTMLDivElement | null>;
    snapButton?: SplitPanelChild;
    snapButtonPos: number = 10;
    snapButtonOpenOffset: number = 0;
    snapButtonClosedOffset: number = 0;
    snapButtonTimeout?: NodeJS.Timeout;
    snapButtonZindex = 1001;
    splitContents?: SplitPanelChild[];
    moved: boolean = false;
    maxSnapThresh: number;
    minSnapThresh: number;

    state: SplitPanelState = {
        size: 30,
        prevSize: 30,
        collapsed: false,
        snapBtnPosLeft: undefined,
        snapBtnPosTop: undefined,
        snapBtnDisplay: false,
        snapBtnIsPressed: false,
        isResizing: false,
        maxReached: false,
        isAnimating: false
    };

    stopResizeHandler: (evt: MouseEvent) => void;
    updateResizeHandler: (evt: MouseEvent) => void;

    constructor(props: SplitPanelProps) {
        super(props);
        this.initProps = {
            ...SplitPanel.defaultProps,
            ...props,
            theme: {
                ...SplitPanel.defaultProps.theme,
                ...props.theme,
            },
        };

        this.panel = React.createRef<HTMLDivElement>();
        this.divider = React.createRef<HTMLDivElement>();
        this.snapButtonContainer = React.createRef<HTMLDivElement>();

        // Cast children to our expected type so we can access .props.slot etc.
        this.snapButton = (props.children as SplitPanelChild[] | undefined)?.find(
            (child) => child.props.slot === 'snap-btn'
        );
        if (this.snapButton?.props['data-btn-pos'])
            this.snapButtonPos = parseFloat(this.snapButton.props['data-btn-pos']);
        if (this.snapButton?.props['data-btn-open-offset'])
            this.snapButtonOpenOffset = parseFloat(this.snapButton.props['data-btn-open-offset']);
        if (this.snapButton?.props['data-btn-closed-offset'])
            this.snapButtonClosedOffset = parseFloat(this.snapButton.props['data-btn-closed-offset']);
        this.splitContents = (props.children as SplitPanelChild[] | undefined)?.filter(
            (child) => !child.props.slot
        );
        this.maxSnapThresh = this.initProps.maxResize - this.initProps.maxResize * 0.05;
        this.minSnapThresh = (this.initProps.collapsedSize || 0) + this.initProps.maxResize * 0.03;

        this.state.snapBtnDisplay = this.initProps.snapButtonTimeout ? false : true;
        this.state.prevSize = this.initProps.initialSize;
        this.state.size = this.initProps.collapsed ? 0 : this.initProps.initialSize;
        this.state.collapsed = this.initProps.collapsed;

        this.stopResizeHandler = (evt: MouseEvent) => this.stopResize(evt);
        this.updateResizeHandler = (evt: MouseEvent) => this.updateResize(evt);
    }

    componentDidUpdate(prevProps: SplitPanelProps, prevState: SplitPanelState) {
        if (
            prevProps.collapseDirection !== this.props.collapseDirection ||
            prevProps.split !== this.props.split ||
            prevProps.theme !== this.props.theme
        ) {
            this.initProps = {
                ...this.initProps,
                ...this.props,
                theme: {
                    ...this.initProps.theme,
                    ...this.props.theme,
                },
            };

            if (this.state.collapsed) {
                this.minimize();
            } else {
                this.setState({ size: this.state.size });
            }
            this.repositionSnapButton();
        }

        // `collapsible` (e.g. results panel: collapsible={showResultsPanel}, false until a
        // search returns rows) is otherwise only captured once in the constructor via
        // initProps, so a later flip to true would never re-render the divider/snap-button -
        // the collapse control would just never exist. Refresh initProps and force a render.
        if (prevProps.collapsible !== this.props.collapsible) {
            this.initProps = { ...this.initProps, collapsible: this.props.collapsible };
            this.forceUpdate(() => this.repositionSnapButton());
        }

        if (this.snapButton && this.state.size !== prevState.size) {
            this.repositionSnapButton();
        }

        if (prevProps.collapsed !== this.props.collapsed) {
            if (this.props.collapsed) {
                this.minimize();
            } else {
                this.expand();
            }
        }
    }

    async repositionSnapButton() {
        const panel = this.panel.current;
        const divider = this.divider.current;
        const snapBtn = this.snapButtonContainer.current;

        if (!panel || !divider || !snapBtn) return;

        const snapBtnLeft =
            this.initProps.split === 'v'
                ? this.initProps.collapseDirection === 'left'
                    ? divider.offsetLeft +
                    (this.state.collapsed ? this.snapButtonClosedOffset : this.snapButtonOpenOffset)
                    : divider.offsetLeft +
                    divider.offsetWidth -
                    (snapBtn.offsetWidth +
                        (this.state.collapsed ? this.snapButtonClosedOffset : this.snapButtonOpenOffset))
                : divider.offsetLeft + panel.offsetWidth * (this.snapButtonPos / 100);

        const snapBtnTop =
            this.initProps.split === 'v'
                ? divider.offsetTop + panel.offsetHeight * (this.snapButtonPos / 100)
                : this.initProps.collapseDirection === 'down'
                    ? divider.offsetTop +
                    divider.offsetHeight -
                    (snapBtn.offsetHeight +
                        (this.state.collapsed ? this.snapButtonClosedOffset : this.snapButtonOpenOffset))
                    : divider.offsetTop + (this.state.collapsed ? this.snapButtonClosedOffset : this.snapButtonOpenOffset);

        await this.setState({
            snapBtnPosLeft: snapBtnLeft,
            snapBtnPosTop: snapBtnTop,
        });
    }

    setSnapButtonIcon() {
        const openIcon = this.snapButtonContainer.current?.querySelector('[slot=snap-btn-open-icon]');
        const closeIcon = this.snapButtonContainer.current?.querySelector('[slot=snap-btn-close-icon]');
        if (this.state.collapsed) {
            closeIcon?.setAttribute('hidden', '');
            openIcon?.removeAttribute('hidden');
        } else {
            openIcon?.setAttribute('hidden', '');
            closeIcon?.removeAttribute('hidden');
        }
    }

    async onSnapBtnClick(evt: React.MouseEvent) {
        if (!this.props.collapsible) return;
        if (this.state.collapsed) {
            await this.expand();
            if (this.initProps.onOpen) this.initProps.onOpen();
        } else {
            await this.minimize();
            if (this.initProps.onClose) this.initProps.onClose();
        }
    }

    onDividerClick(evt?: React.MouseEvent) {
        if (!this.props.collapsible) return;
        if (this.moved) return;
        if (this.state.collapsed) {
            this.expand();
            if (this.initProps.onOpen) this.initProps.onOpen();
        } else {
            this.minimize();
            if (this.initProps.onClose) this.initProps.onClose();
        }
    }

    async onDividerMouseEnter(evt: React.MouseEvent) {
        if (this.initProps.snapButtonTimeout) {
            await this.setState({ snapBtnDisplay: true });
            this.repositionSnapButton();
            clearTimeout(this.snapButtonTimeout);
        }
    }

    onDividerMouseLeave(evt: React.MouseEvent) {
        if (this.initProps.snapButtonTimeout) {
            this.snapButtonTimeout = setTimeout(
                () => this.setState({ snapBtnDisplay: false }),
                this.initProps.snapButtonTimeout
            );
        }
    }

    async expand() {
        this.setState({ isAnimating: true });
        await this.setState((state) => ({
            size: state.prevSize <= this.minSnapThresh ? this.initProps.initialSize : state.prevSize,
            collapsed: false,
        }));
        this.setSnapButtonIcon();
        window.dispatchEvent(new Event('split-panel-resize'));
        window.setTimeout(() => this.setState({ isAnimating: false }), this.initProps.animDurationMs);
    }

    async minimize() {
        this.setState({ isAnimating: true });
        await this.setState((state) => ({
            size: this.initProps.collapsedSize,
            prevSize: state.size,
            collapsed: true,
        }));
        this.setSnapButtonIcon();
        window.dispatchEvent(new Event('split-panel-resize'));
        window.setTimeout(() => this.setState({ isAnimating: false }), this.initProps.animDurationMs);
    }

    maximize() {
        this.setState({ size: this.initProps.maxResize });
        window.dispatchEvent(new Event('split-panel-resize'));
    }

    startResize(evt: React.MouseEvent) {
        evt.preventDefault();
        this.moved = false;
        window.addEventListener('mouseup', this.stopResizeHandler);
        window.addEventListener('mousemove', this.updateResizeHandler);
    }

    async updateResize(evt: MouseEvent) {
        evt.preventDefault();
        if (this.state.collapsed) return;
        if (!evt.view) return;
        this.setState({ isResizing: true });
        this.moved = true;
        const panelEl = this.panel.current;
        if (!panelEl) return;
        const panelH = panelEl.offsetHeight;
        const panelW = panelEl.offsetWidth;
        const mouseVPos = evt.pageY - panelEl.offsetTop;
        const mouseHPos = evt.pageX - panelEl.offsetLeft;
        if (this.initProps.split === 'v') {
            if (this.initProps.collapseDirection === 'left') {
                const newSize = (mouseHPos / panelW) * 100;
                if (newSize >= this.initProps.maxResize || newSize <= this.initProps.collapsedSize) {
                    await this.setState({ maxReached: true });
                    return;
                }
                await this.setState({ size: newSize, maxReached: false });
            } else {
                const newSize = ((panelW - mouseHPos) / panelW) * 100;
                if (newSize >= this.initProps.maxResize || newSize <= this.initProps.collapsedSize) {
                    await this.setState({ maxReached: true });
                    return;
                }
                await this.setState({ size: newSize, maxReached: false });
            }
        } else {
            if (this.initProps.collapseDirection === 'down') {
                const newSize = ((panelH - mouseVPos) / panelH) * 100;
                if (newSize >= this.initProps.maxResize || newSize <= this.initProps.collapsedSize) {
                    await this.setState({ maxReached: true });
                    return;
                }
                await this.setState({ size: newSize, maxReached: false });
            } else {
                const newSize = (mouseVPos / panelH) * 100;
                if (newSize >= this.initProps.maxResize || newSize <= this.initProps.collapsedSize) {
                    await this.setState({ maxReached: true });
                    return;
                }
                await this.setState({ size: newSize, maxReached: false });
            }
        }
        window.dispatchEvent(new Event('split-panel-resize'));
    }

    async stopResize(evt?: MouseEvent) {
        this.setState({ isResizing: false, maxReached: false });
        if (this.state.size >= this.maxSnapThresh) await this.maximize();
        if (this.state.size <= this.minSnapThresh) await this.minimize();
        this.setSnapButtonIcon();
        window.removeEventListener('mousemove', this.updateResizeHandler);
        window.removeEventListener('mouseup', this.stopResizeHandler);
    }

    render() {
        const split1Collapse =
            (this.initProps.split === 'v' && this.initProps.collapseDirection === 'left') ||
            (this.initProps.split === 'h' && this.initProps.collapseDirection === 'up');
    
        const split2Collapse =
            (this.initProps.split === 'v' && this.initProps.collapseDirection === 'right') ||
            (this.initProps.split === 'h' && this.initProps.collapseDirection === 'down');
    
        // Read collapsible from the live prop, not initProps - initProps.collapsible is only
        // refreshed reactively via the componentDidUpdate patch above; reading props directly
        // here means the divider/snap-button can never get stuck stale regardless of update
        // timing (defaultProps guarantees this.props.collapsible is always defined).
        const isSingle = !this.props.collapsible;
        const effectiveSize = this.state.size;
    
        // Re-derive the snap-button element from the live children every render. this.snapButton
        // (set once in the constructor) is a frozen snapshot of that React element - reusing it
        // directly would also freeze whatever dynamic props it carried at mount time (e.g. the
        // results-panel button's `hidden={!showResultsPanel}`, which is true before any search
        // has run), so it would stay hidden forever even after results arrive and re-render.
        const liveSnapButton =
            (this.props.children as SplitPanelChild[] | undefined)?.find(
                (child) => child.props.slot === 'snap-btn'
            ) ?? this.snapButton;

        const firstChild = (this.props.children as SplitPanelChild[] | undefined)?.find((c) =>
            c.props.slot === (split1Collapse ? 'collapse-content' : 'main-content')
        );
    
        const secondChild = (this.props.children as SplitPanelChild[] | undefined)?.find((c) =>
            c.props.slot === (split2Collapse ? 'collapse-content' : 'main-content')
        );
    
        const hasFixedHeight = !!this.initProps.fixedHeight;
        
        // For non-collapsible panels with fixed height, use fixed sizing
        const useFixedSize = isSingle && hasFixedHeight;
    
        return (
            <Panel split={this.initProps.split} ref={this.panel} style={this.initProps.style}>
                <Split
                    split={this.initProps.split}
                    collapse={split1Collapse}
                    size={effectiveSize}
                    maxResize={split1Collapse ? this.initProps.maxResize : undefined}
                    panel={this.panel.current!}
                    animate={this.initProps.animate && !this.state.isResizing && !isSingle}
                    animDurationMs={this.initProps.animDurationMs}
                    animEasing={this.initProps.animEasing}
                    animFreezeWrap={!!this.state.isAnimating}
                    fixedHeight={useFixedSize && split1Collapse ? this.initProps.fixedHeight : undefined}
                    isComplement={useFixedSize && !split1Collapse}
                >
                    {firstChild}
                </Split>
    
                {!isSingle && (
                    <Divider
                        ref={this.divider}
                        collapsed={this.state.collapsed}
                        collapseDirection={this.initProps.collapseDirection}
                        split={this.initProps.split}
                        label={this.initProps.label}
                        labelAlign={this.initProps.labelAlign}
                        snapButton={liveSnapButton}
                        isResizing={this.state.isResizing}
                        maxReached={this.state.maxReached}
                        resizable={!isSingle && this.initProps.resizable}
                        resizeAssist={!isSingle && this.initProps.resizeAssist}
                        onMouseDown={!isSingle && this.initProps.resizable ? this.startResize.bind(this) : undefined}
                        onClick={!isSingle && !liveSnapButton ? this.onDividerClick.bind(this) : undefined}
                        onMouseEnter={!isSingle ? this.onDividerMouseEnter.bind(this) : (() => { })}
                        onMouseLeave={!isSingle ? this.onDividerMouseLeave.bind(this) : (() => { })}
                        theme={this.initProps.theme}
                        zIndex={this.dividerZindex}
                        btnPos={this.snapButtonPos}
                        btnOpenOffset={this.snapButtonOpenOffset}
                        btnClosedOffset={this.snapButtonClosedOffset}
                        onSnapBtnClick={!isSingle ? this.onSnapBtnClick.bind(this) : undefined}
                        isCollapsed={this.state.collapsed}
                        styleOverride={isSingle ? { display: 'none' } : undefined as any}
                    />
                )}
    
                <Split
                    split={this.initProps.split}
                    collapse={split2Collapse}
                    size={effectiveSize}
                    maxResize={split2Collapse ? this.initProps.maxResize : undefined}
                    panel={this.panel.current!}
                    animate={this.initProps.animate && !this.state.isResizing && !isSingle}
                    animDurationMs={this.initProps.animDurationMs}
                    animEasing={this.initProps.animEasing}
                    animFreezeWrap={!!this.state.isAnimating}
                    fixedHeight={useFixedSize && split2Collapse ? this.initProps.fixedHeight : undefined}
                    isComplement={useFixedSize && !split2Collapse}
                >
                    {secondChild}
                </Split>
            </Panel>
        );
    }
}

interface PanelProps {
    split: 'v' | 'h';
    style?: React.CSSProperties;
    children: React.ReactNode;
}

const Panel = React.forwardRef<HTMLDivElement, PanelProps>((props, ref) => (
    <div
        ref={ref}
        style={{
            ...props.style,
            display: 'flex',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            flexDirection: props.split === 'v' ? 'row' : 'column',
        }}
    >
        {props.children}
    </div>
));

interface SplitProps {
    split: 'v' | 'h';
    size: number;
    collapse?: boolean;
    maxResize?: number;
    panel: HTMLDivElement;
    children?: React.ReactNode;
    animate?: boolean;
    animDurationMs?: number;
    animEasing?: string;
    animFreezeWrap?: boolean;
    fixedHeight?: string;
    isComplement?: boolean;
}

const Split = (props: SplitProps) => {
    const animate = props.animate ?? false;
    const dur = props.animDurationMs ?? 220;
    const ease = props.animEasing ?? 'ease';

    // If this is the complement to a fixed-height panel, it should flex to fill remaining space
    if (props.isComplement) {
        return (
            <div
                className={props.animFreezeWrap ? 'split-no-wrap' : undefined}
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    minHeight: 0,
                }}
            >
                {props.children}
            </div>
        );
    }

    // If fixedHeight is provided, use fixed dimensions
    if (props.fixedHeight) {
        return (
            <div
                className={props.animFreezeWrap ? 'split-no-wrap' : undefined}
                style={{
                    height: props.split === 'h' ? props.fixedHeight : '100%',
                    width: props.split === 'v' ? props.fixedHeight : '100%',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    minHeight: 0,
                    flexShrink: 0,
                    flexGrow: 0,
                }}
            >
                {props.children}
            </div>
        );
    }

    let height = `${props.size}%`;
    let width = `${props.size}%`;

    if (props.split === 'v') {
        height = '100%';
        width = props.collapse
            ? (props.size === props.maxResize && props.maxResize ? `${props.maxResize}%` : `calc(${props.size}%)`)
            : `calc(${100 - props.size}%)`;
    } else {
        width = '100%';
        height = props.collapse
            ? (props.size === props.maxResize && props.maxResize ? `${props.maxResize}%` : `calc(${props.size}%)`)
            : `calc(${100 - props.size}%)`;
    }

    const transitionProp = props.split === 'v' ? 'width' : 'height';

    return (
        <div
            className={props.animFreezeWrap ? 'split-no-wrap' : undefined}
            style={{
                height,
                width,
                overflow: 'hidden',
                boxSizing: 'border-box',
                minWidth: 0,
                minHeight: 0,
                transition: animate ? `${transitionProp} ${dur}ms ${ease}` : 'none',
                willChange: animate ? transitionProp : undefined,
                contain: animate ? 'layout paint' : undefined,
            }}
        >
            {props.children}
        </div>
    );
};
interface DividerProps {
    split: 'v' | 'h';
    collapsed: boolean;
    collapseDirection: 'left' | 'right' | 'up' | 'down';
    label?: React.ReactNode;
    labelAlign?: 'start' | 'center' | 'end';
    snapButton?: SplitPanelChild;
    maxReached?: boolean;
    isResizing?: boolean;
    resizable?: boolean;
    resizeAssist?: boolean;
    theme: SplitPanelTheme;
    zIndex: number;
    onMouseDown?: (evt: React.MouseEvent) => void;
    onClick?: (evt: React.MouseEvent) => void;
    onMouseEnter: (evt: React.MouseEvent) => void;
    onMouseLeave: (evt: React.MouseEvent) => void;
    btnPos?: number;
    btnOpenOffset?: number;
    btnClosedOffset?: number;
    onSnapBtnClick?: (evt: React.MouseEvent) => void;
    isCollapsed?: boolean;
    styleOverride?: React.CSSProperties;
}

const Divider = React.forwardRef<HTMLDivElement, DividerProps>((props, ref) => {
    const [isHovering, setIsHovering] = React.useState<boolean>(false);
    const [isPressed, setIsPressed] = React.useState<boolean>(false);

    const theme = { dividerBorderWidth: 1, ...props.theme };

    const onMouseEnter = (evt: React.MouseEvent) => {
        setIsHovering(true);
        props.onMouseEnter(evt);
    };

    const onMouseLeave = (evt: React.MouseEvent) => {
        setIsHovering(false);
        props.onMouseLeave(evt);
    };

    const onMouseDown = (evt: React.MouseEvent) => {
        if (props.onMouseDown) props.onMouseDown(evt);
        setIsPressed(true);
    };

    const onMouseUp = (evt: React.MouseEvent) => {
        setIsPressed(false);
    };

    const btnInnerRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const root = btnInnerRef.current;
        if (!root) return;
        const openIcon = root.querySelector('[slot="snap-btn-open-icon"]') as HTMLElement | null;
        const closeIcon = root.querySelector('[slot="snap-btn-close-icon"]') as HTMLElement | null;
        if (props.isCollapsed) {
            closeIcon?.setAttribute('hidden', '');
            openIcon?.removeAttribute('hidden');
        } else {
            openIcon?.setAttribute('hidden', '');
            closeIcon?.removeAttribute('hidden');
        }
    }, [props.isCollapsed]);

    const posStyle: React.CSSProperties = {};
    const btnPos = Math.max(0, Math.min(100, props.btnPos ?? 50));
    const openOffset = props.btnOpenOffset ?? 0;
    const closedOffset = props.btnClosedOffset ?? 0;
    const offsetPx = props.isCollapsed ? closedOffset : openOffset;

    if (props.split === 'v') {
        posStyle.top = `${btnPos}%`;
        posStyle.transform = 'translate(-50%, -50%)';
        if (props.collapseDirection === 'left') {
            posStyle.left = `${offsetPx}px`;
        } else {
            posStyle.right = `${offsetPx}px`;
            posStyle.left = 'auto';
            posStyle.transform = 'translate(50%, -50%)';
        }
    } else {
        posStyle.left = `${btnPos}%`;
        posStyle.transform = 'translate(-50%, -50%)';
        if (props.collapseDirection === 'down') {
            posStyle.bottom = `${offsetPx}px`;
        } else {
            posStyle.top = `${offsetPx}px`;
        }
    }

    return (
        <div
            ref={ref}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onClick={props.onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                zIndex: props.zIndex,
                display: 'flex',
                position: 'relative',
                flexDirection: 'row',
                justifyContent: props.labelAlign,
                alignItems: 'center',
                boxSizing: 'border-box',
                lineHeight: '1.5',
                color: theme.collapsedLabelColor,
                borderTopWidth: props.split === 'h' ? theme.dividerBorderWidth : 0,
                borderRightWidth: props.split === 'v' ? theme.dividerBorderWidth : 0,
                borderBottomWidth: props.split === 'h' ? theme.dividerBorderWidth : 0,
                borderLeftWidth: props.split === 'v' ? theme.dividerBorderWidth : 0,
                borderTopColor: theme.dividerBorderColor,
                borderRightColor: theme.dividerBorderColor,
                borderBottomColor: theme.dividerBorderColor,
                borderLeftColor: theme.dividerBorderColor,
                fontFamily: theme.fontFamily,
                fontSize: theme.fontSize,
                fontWeight: theme.fontWeight,
                borderTopStyle: 'solid',
                borderRightStyle: 'solid',
                borderBottomStyle: 'solid',
                borderLeftStyle: 'solid',
                height:
                    props.split === 'v'
                        ? '100%'
                        : props.collapsed
                            ? theme.collapsedWidth
                            : props.resizeAssist && (props.isResizing || (props.resizable && isHovering))
                                ? '20px'
                                : `${theme.dividerWidth}px`,
                width:
                    props.split === 'v'
                        ? props.collapsed
                            ? theme.collapsedWidth
                            : props.resizeAssist && (props.isResizing || (props.resizable && isHovering))
                                ? '20px'
                                : `${theme.dividerWidth}px`
                        : '100%',
                padding: props.split === 'v' ? '0.5% 0 0 0' : '0 0 0 0.5%',
                backgroundColor: props.collapsed
                    ? theme.collapsedBgColor
                    : props.isResizing || (isHovering && (props.resizable || !props.snapButton))
                        ? props.maxReached
                            ? theme.maxReachedColor
                            : theme.dividerHoverColor
                        : theme.dividerColor,
                boxShadow: isHovering
                    ? (props.resizable || !props.snapButton) && !props.collapsed
                        ? isPressed
                            ? 'black 0px 0px 8px 1px'
                            : 'black 0px 0px 13px 1px'
                        : undefined
                    : undefined,
                writingMode: props.split === 'v' ? 'vertical-rl' : 'horizontal-tb',
                filter: isHovering && props.collapsed && !props.snapButton ? 'brightness(120%)' : '',
                cursor: isHovering
                    ? props.resizable && !props.collapsed
                        ? props.split === 'v'
                            ? 'ew-resize'
                            : 'ns-resize'
                        : props.snapButton
                            ? undefined
                            : 'pointer'
                    : undefined,
                ...(props.styleOverride || {}),
            }}
        >
            {props.collapsed && props.label}
            {props.snapButton && (
                <div
                    ref={btnInnerRef}
                    onClick={props.onSnapBtnClick}
                    style={{
                        position: 'absolute',
                        ...posStyle,
                    }}
                >
                    {props.snapButton}
                </div>
            )}
        </div>
    );
});
